import { formatDate } from '@/shared/lib/dateUtils'
import type { DashboardMetrics, DashboardApiResponse } from '../types/dashboard';
import { config, endpoints } from '../config/env';

export class DashboardApiError extends Error {
  status?: number;
  code?: string;

  constructor(
    message: string,
    status?: number,
    code?: string
  ) {
    super(message);
    this.name = 'DashboardApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Fetch dashboard metrics from the backend
 * @param token - JWT token for authentication
 * @returns Promise with dashboard metrics data
 */
export async function fetchDashboardMetrics(token: string): Promise<DashboardMetrics> {
  if (!token) {
    throw new DashboardApiError('No authentication token provided', 401, 'NO_TOKEN');
  }

  try {
    const response = await fetch(`${config.apiBaseUrl}${endpoints.dashboard.metrics}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If not JSON, use default error message
      }

      // 🔐 MANEJO DE TOKEN EXPIRADO
      if (response.status === 401) {
        console.error('🚨 [Dashboard API] TOKEN EXPIRADO - Emitiendo evento de sesión no autorizada')
        
        // Limpiar token
        localStorage.removeItem('access_token')
        
        // Emitir evento para que el hook useAuth lo capture
        window.dispatchEvent(new CustomEvent('auth:unauthorized', {
          detail: { message: errorMessage || 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.' }
        }))
      }

      throw new DashboardApiError(
        errorMessage,
        response.status,
        response.status === 401 ? 'UNAUTHORIZED' : 'API_ERROR'
      );
    }

    const data: DashboardApiResponse = await response.json();
    
    if (!data.success) {
      throw new DashboardApiError(
        data.message || 'API returned unsuccessful response',
        500,
        'API_FAILURE'
      );
    }

    return data.data;
  } catch (error) {
    if (error instanceof DashboardApiError) {
      throw error;
    }

    // Network or other errors
    if (error instanceof Error) {
      throw new DashboardApiError(
        `Network error: ${error.message}`,
        0,
        'NETWORK_ERROR'
      );
    }

    throw new DashboardApiError(
      'Unknown error occurred while fetching dashboard metrics',
      0,
      'UNKNOWN_ERROR'
    );
  }
}

/**
 * Export CSV data from job requests
 * @param data - Array of job request updates
 * @returns CSV string
 */
export function exportJobRequestsToCSV(data: DashboardMetrics['jobRequests']['latestUpdates']): string {
  if (!data || data.length === 0) {
    return '';
  }

  const headers = [
    'ID',
    'Area',
    'Puesto',
    'Prioridad', 
    'Estado',
    'Vacantes',
    'Solicitante',
    'Email Solicitante',
    'Fecha Creación',
    'Última Actualización'
  ];

  const csvRows = [
    headers.join(','),
    ...data.map(item => [
      item.id.toString(),
      `"${item.areaVacante}"`,
      `"${item.puestoSolicitar}"`,
      item.prioridad,
      item.status,
      item.numeroPlazas.toString(),
      `"${item.solicitante?.nombreCompleto || 'N/A'}"`,
      `"${item.solicitante?.email || 'N/A'}"`,
      formatDate(item.createdAt),
      formatDate(item.updatedAt)
    ].join(','))
  ];

  return csvRows.join('\n');
}

/**
 * Export and download CSV data from job requests
 * @param data - Array of job request updates  
 * @param filename - Optional filename
 */
export function exportToCsv(data: DashboardMetrics['jobRequests']['latestUpdates'], filename: string = 'solicitudes-vacantes'): void {
  const csvContent = exportJobRequestsToCSV(data);
  const fullFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  downloadCSV(csvContent, fullFilename);
}

/**
 * Download CSV file
 * @param csvContent - CSV string content
 * @param filename - Name for the downloaded file
 */
export function downloadCSV(csvContent: string, filename: string = 'solicitudes-vacantes.csv'): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}